

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import { FiDownload, FiFilter, FiRefreshCw, FiSearch, FiUsers } from "react-icons/fi";
import { baseApiURL } from "../../baseUrl";

const FacultyData = () => {
  const [faculties, setFaculties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [experienceFilter, setExperienceFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchFaculties = () => {
    setLoading(true);
    axios
      .get(`${baseApiURL()}/faculty/details/getDetails2`)
      .then((res) => {
        if (res.data.success) {
          setFaculties(res.data.faculties || []);
        } else {
          toast.error(res.data.message || "Unable to load faculty data");
        }
      })
      .catch((error) =>
        toast.error(error.response?.data?.message || "Unable to load faculty data")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchFaculties();
  }, []);

  const departments = useMemo(() => {
    const unique = new Set(
      faculties.map((faculty) => faculty.department).filter((dept) => !!dept)
    );
    return ["all", ...unique];
  }, [faculties]);

  const filteredFaculties = useMemo(() => {
    return faculties.filter((faculty) => {
      const matchesDept =
        departmentFilter === "all" ||
        (faculty.department || "").toLowerCase() === departmentFilter.toLowerCase();
      const exp = Number(faculty.experience) || 0;
      const matchesExp =
        !experienceFilter || exp >= Number(experienceFilter || 0);
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        !term ||
        faculty.employeeId?.toLowerCase().includes(term) ||
        faculty.firstName?.toLowerCase().includes(term) ||
        faculty.lastName?.toLowerCase().includes(term) ||
        faculty.email?.toLowerCase().includes(term);
      return matchesDept && matchesExp && matchesSearch;
    });
  }, [faculties, departmentFilter, experienceFilter, searchTerm]);

  const stats = useMemo(() => {
    const total = faculties.length;
    const totalExperience = faculties.reduce(
      (sum, faculty) => sum + (Number(faculty.experience) || 0),
      0
    );
    const avgExperience = total ? (totalExperience / total).toFixed(1) : "0.0";
    const recent = faculties.filter(
      (faculty) => (Number(faculty.experience) || 0) <= 1
    ).length;
    return {
      total,
      avgExperience,
      deptCount: departments.length - 1,
      recent,
    };
  }, [faculties, departments]);

  const exportToExcel = () => {
    if (filteredFaculties.length === 0) {
      toast.error("No faculty records to export");
      return;
    }
    const data = filteredFaculties.map((faculty) => ({
      "Employee ID": faculty.employeeId,
      Name: `${faculty.firstName || ""} ${faculty.lastName || ""}`.trim(),
      Email: faculty.email,
      Phone: faculty.phoneNumber,
      Department: faculty.department,
      Experience: faculty.experience,
      "Designation": faculty.post,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Faculty");
    XLSX.writeFile(workbook, "faculty_directory.xlsx");
    toast.success("Faculty data exported");
  };

  const formatPhone = (value) => {
    if (!value) return "—";
    return value.toString().replace(/(\d{3})(\d{3})(\d+)/, "$1-$2-$3");
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
        <div className="bento-header-banner flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
              <FiUsers className="text-xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Faculty Directory</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                Quick access to faculty contacts, departments, and experience.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchFaculties}
              className="px-3.5 py-1.5 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer flex items-center gap-1.5 shadow-xs"
              disabled={loading}
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportToExcel}
              className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5 shadow-xs"
            >
              <FiDownload />
              Export
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Faculty</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
            </div>
            <div className="p-4 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-xl">
              <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Departments</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                {stats.deptCount}
              </p>
            </div>
            <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-800/60 rounded-xl">
              <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Avg. Experience</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">
                {stats.avgExperience} yrs
              </p>
            </div>
            <div className="p-4 bg-amber-50/60 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-800/60 rounded-xl">
              <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">New Joinees (&le; 1 yr)</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">{stats.recent}</p>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-xs uppercase tracking-wider">
              <FiFilter className="text-indigo-600 dark:text-indigo-400" />
              Filters
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or ID"
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept === "all" ? "All Departments" : dept}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <input
                  type="number"
                  min="0"
                  value={experienceFilter}
                  onChange={(e) => setExperienceFilter(e.target.value)}
                  placeholder="Min experience (years)"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Employee</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                  <th className="px-4 py-3 text-left">Department</th>
                  <th className="px-4 py-3 text-left">Experience</th>
                  <th className="px-4 py-3 text-left">Designation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                {filteredFaculties.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-400 text-xs">
                      {loading ? "Loading faculty..." : "No matching records found."}
                    </td>
                  </tr>
                )}
                {filteredFaculties.map((faculty) => (
                  <tr key={faculty._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-900 dark:text-white">
                        {faculty.firstName} {faculty.lastName}
                      </p>
                      <p className="text-[11px] text-slate-400 font-mono">{faculty.employeeId}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold">{faculty.email || "—"}</p>
                      <p className="text-[11px] text-slate-400">{formatPhone(faculty.phoneNumber)}</p>
                    </td>
                    <td className="px-4 py-3">{faculty.department || "—"}</td>
                    <td className="px-4 py-3">
                      {(Number(faculty.experience) || 0).toFixed(1)} yrs
                    </td>
                    <td className="px-4 py-3">{faculty.post || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacultyData;